// „Was kann ich kochen?" und „Reicht es für …?".
// Ablaufende Zutaten werden bevorzugt verwertet – Vorrat retten statt wegwerfen.
import { api } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import { state } from '../core/store.js';
import { clear, el, replace } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';

interface SuggestionIngredient {
  name: string;
  amount: number | null;
  unit: string | null;
}

interface Suggestion {
  name: string;
  missing?: string[];
  usesExpiring?: string[];
  instructions?: string | null;
  ingredients: SuggestionIngredient[];
}

export async function openCookIdeas(): Promise<void> {
  const expiring = [...new Set(state.expiring.map((i) => i.name))];

  const body = el('div', {}, [
    expiring.length
      ? el(
          'p',
          { class: 'notice notice-warn' },
          `Bald fällig: ${expiring.slice(0, 5).join(', ')} – Vorschläge, die das verwerten, kommen zuerst.`,
        )
      : null,
    el('div', { class: 'loading-block' }, [
      el('span', { class: 'spinner' }),
      el('p', { class: 'muted' }, 'Ideen werden gesucht …'),
    ]),
  ]);
  const sheet = openSheet(body, { title: 'Was kann ich kochen?' });

  const result = await guard(
    () =>
      api<{ suggestions: Suggestion[]; usedExpiring: string[] }>('/api/recipes/suggest', {
        method: 'POST',
        body: {},
      }),
    { failure: 'Vorschläge' },
  );

  body.querySelector('.loading-block')?.remove();

  if (!result) {
    body.append(
      el(
        'p',
        { class: 'error-box' },
        'Ideen brauchen einen hinterlegten KI-Schlüssel (GEMINI_API_KEY). Eigene Rezepte funktionieren auch ohne.',
      ),
    );
    return;
  }
  if (!result.suggestions.length) {
    body.append(
      el('p', { class: 'muted' }, 'Gerade keine Vorschläge – vielleicht ist der Vorrat zu leer.'),
    );
    return;
  }

  // Was Ablaufendes verwertet, steht oben.
  const sorted = [...result.suggestions].sort(
    (a, b) => (b.usesExpiring?.length ?? 0) - (a.usesExpiring?.length ?? 0),
  );

  for (const suggestion of sorted) {
    body.append(suggestionCard(suggestion, sheet.close));
  }
}

function suggestionCard(s: Suggestion, close: () => void): HTMLElement {
  const ingredients = s.ingredients
    .map((i) => `${i.amount ? `${i.amount}${i.unit ? ` ${i.unit}` : ''} ` : ''}${i.name}`)
    .join(', ');

  return el('div', { class: 'card' }, [
    el('div', { class: 'item-name' }, s.name),
    s.usesExpiring?.length
      ? el('div', { class: 'item-meta' }, [
          el('span', { class: 'badge badge-warn' }, `verwertet ${s.usesExpiring.join(', ')}`),
        ])
      : null,
    el('p', { class: 'tiny' }, ingredients || '—'),
    s.missing?.length ? el('p', { class: 'tiny' }, `Fehlt evtl.: ${s.missing.join(', ')}`) : null,
    el('div', { class: 'btn-row' }, [
      s.missing?.length
        ? el(
            'button',
            {
              class: 'btn btn-sm btn-secondary',
              type: 'button',
              onclick: guarded(
                async (e) => {
                  for (const name of s.missing ?? []) {
                    await api('/api/shopping', { method: 'POST', body: { freeText: name } });
                  }
                  (e.currentTarget as HTMLButtonElement).disabled = true;
                },
                { success: 'Fehlendes steht auf der Liste' },
              ),
            },
            [icon('cart', { size: 16 }), 'Fehlendes auf die Liste'],
          )
        : null,
      el(
        'button',
        {
          class: 'btn btn-sm btn-secondary',
          type: 'button',
          onclick: guarded(
            async () => {
              await api('/api/recipes/from-suggestion', {
                method: 'POST',
                body: { suggestion: s },
              });
              close();
            },
            { success: `„${s.name}" als Rezept gespeichert` },
          ),
        },
        [icon('plus', { size: 16 }), 'Als Rezept speichern'],
      ),
    ]),
  ]);
}

export function openFeasibilityCheck(): void {
  const input = el('input', {
    class: 'input',
    type: 'search',
    placeholder: 'z.B. Gulasch',
    enterkeyhint: 'search',
  });
  const servings = el('input', {
    class: 'input',
    type: 'number',
    min: 1,
    max: 50,
    value: '',
    placeholder: 'Portionen (optional)',
  });
  const result = el('div', { class: 'screen-section' });

  const body = el('div', {}, [
    el('div', { class: 'field' }, [el('label', {}, 'Für welches Gericht?'), input]),
    el('div', { class: 'field' }, [el('label', {}, 'Portionen'), servings]),
    result,
  ]);
  const sheet = openSheet(body, { title: 'Reicht es für …?' });

  const check = async (): Promise<void> => {
    const name = input.value.trim();
    if (!name) return;
    replace(result, el('span', { class: 'spinner' }));

    const data = await guard(
      () =>
        api<{
          found: boolean;
          feasible?: boolean;
          sentence: string;
          recipe?: { name: string };
          missing?: unknown[];
        }>('/api/feasibility', {
          method: 'POST',
          body: { recipe: name, servings: servings.value ? Number(servings.value) : null },
        }),
      { failure: 'Prüfung fehlgeschlagen' },
    );
    if (!data) {
      clear(result);
      return;
    }

    replace(result, [
      el('p', { class: data.feasible ? 'notice' : 'notice notice-warn' }, data.sentence),
      data.found && !data.feasible && data.missing?.length
        ? el(
            'button',
            {
              class: 'btn btn-primary btn-block',
              type: 'button',
              onclick: guarded(
                async () => {
                  await api('/api/feasibility/add-missing', {
                    method: 'POST',
                    body: {
                      recipe: data.recipe?.name ?? name,
                      servings: servings.value ? Number(servings.value) : null,
                    },
                  });
                  sheet.close();
                },
                { success: 'Fehlendes steht auf der Einkaufsliste' },
              ),
            },
            [icon('cart', { size: 18 }), 'Fehlendes auf die Einkaufsliste'],
          )
        : null,
    ]);
  };

  input.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') void check();
  });

  sheet.addActions(
    el(
      'button',
      { class: 'btn btn-primary btn-block', type: 'button', onclick: () => void check() },
      'Prüfen',
    ),
  );
}

export { toast };
